import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';


import MindmapVisualizer from './services/MindmapVisualizer.js';
import MindmapContent from './services/MindmapContent.js';
import PDFUpload from './services/PDFUpload.js';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js'

import generatePDF from './services/generatePDF.js'

import { ref, get , set} from "firebase/database";
import { db } from './firebase.js';


export const generateMindmapStructure = (phaseData, frameworkPhases, useCase, currentFrameworkPhase) => {
  const newNodes = [];
  const newEdges = [];

  // Helper function to get category colors
  console.log('Generating mindmap structure with phaseData:', phaseData, 'useCase:', useCase, 'currentFrameworkPhase:', currentFrameworkPhase);
  const getCategoryColor = (currentFrameworkPhase) => {
    const colors = {
      skills: '#62BB67',
      knowledge: '#378FC0', 
      mindset: '#E4A204',
      tools: '#7C1586'
    };
    return colors[currentFrameworkPhase] || '#6B7280';
  };

  // Create center node for the project
  newNodes.push({
    id: 'center',
    label: useCase || 'Design Project',
    x: 400,
    y: 300,
    type: 'center',
    color: '#e1dbff'
  });

  // Create phase nodes around the center with better spacing
  const phaseRadius = 120;
  frameworkPhases.forEach((phaseName, phaseIndex) => {
    const phaseKey = phaseName.toLowerCase();
    const currentPhaseData = phaseData[phaseKey];
    
    if (currentPhaseData && currentPhaseData.abilities && currentPhaseData.abilities.length > 0) {
      const angle = (phaseIndex / frameworkPhases.length) * 2 * Math.PI;
      const phaseNodeId = `phase_${phaseKey}`;
      
      const phaseX = 400 + Math.cos(angle) * phaseRadius;
      const phaseY = 300 + Math.sin(angle) * phaseRadius;
      
      newNodes.push({
        id: phaseNodeId,
        label: phaseName,
        x: phaseX,
        y: phaseY,
        type: 'phase',
        color: phaseIndex === frameworkPhases.indexOf(currentFrameworkPhase) ? '#e1dbff' : '#e1dbff'
      });

      newEdges.push({
        id: `center_to_${phaseKey}`,
        source: 'center',
        target: phaseNodeId
      });

      // Add ability nodes for this phase with better positioning
      const abilityRadius = 80;
      currentPhaseData.abilities.forEach((ability, abilityIndex) => {
        const abilityAngle = angle + (abilityIndex - (currentPhaseData.abilities.length - 1) / 2) * 0.8;
        const abilityNodeId = `${phaseKey}_ability_${abilityIndex}`;
        
        const abilityX = phaseX + Math.cos(abilityAngle) * abilityRadius;
        const abilityY = phaseY + Math.sin(abilityAngle) * abilityRadius;
        
        newNodes.push({
          id: abilityNodeId,
          label: ability,
          x: abilityX,
          y: abilityY,
          type: 'ability',
          color: '#e1dbff'
        });

        newEdges.push({
          id: `${phaseNodeId}_to_${abilityNodeId}`,
          source: phaseNodeId,
          target: abilityNodeId
        });

        // Add category nodes (skills, knowledge, mindset, tools)
        const categories = ['skills', 'knowledge', 'mindset', 'tools'];
        const categoryRadius = 50;
        
        categories.forEach((category, catIndex) => {
          const categoryData = currentPhaseData[`${ability}_${category}`];
          if (categoryData && categoryData.length > 0) {
            const catAngle = abilityAngle + (catIndex - 1.5) * 0.5;
            
            categoryData.forEach((item, itemIndex) => {
              const itemNodeId = `${abilityNodeId}_${category}_${itemIndex}`;
              const itemAngle = catAngle + (itemIndex - (categoryData.length - 1) / 2) * 0.3;
              
              const itemX = abilityX + Math.cos(itemAngle) * categoryRadius;
              const itemY = abilityY + Math.sin(itemAngle) * categoryRadius;
              
              newNodes.push({
                id: itemNodeId,
                label: item,
                x: itemX,
                y: itemY,
                type: category,
                color: getCategoryColor(category),
                size: 'small'
              });

              newEdges.push({
                id: `${abilityNodeId}_to_${itemNodeId}`,
                source: abilityNodeId,
                target: itemNodeId
              });
            });
          }
        });
      });
    }
  });

  return { nodes: newNodes, edges: newEdges };
};


function App() {

    const [data, setData] = useState(null); // variable to store DB
  
    useEffect(() => {
      if (!db) return; // backend unavailable during migration to AWS
      const rootRef = ref(db, "/"); // root of your database
      get(rootRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            console.log("Imported DB:", snapshot.val().designData);
            setData(snapshot.val().designData); // store DB in state
          } else {
            console.log("No data found!");
          }
        })
        .catch((error) => {
          console.error("Error importing DB:", error);
        });
    }, []); // empty dependency array = run once on mount
  

  // Mindmap Information of user's conversation
  const [mindmap, setMindmap] = useState({
    empathy: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}},
    define: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}},
    ideate: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}},
    prototype: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}},
    test: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}},
    communicate: {abilities: {},skills: {},knowledge: {},mindset:{},tools: {}}
  });

  const [pdfMode, setPdfMode] = useState(false);


  const handleNodeClick = (node) => {
    console.log('Node clicked:', node);
  };
  const [chatEnabled, setChatEnabled] = useState(true);



  // State for sessions management
  const [sessions, setSessions] = useState([
    { 
      id: 1, 
      title: "First conversation", 
      mindmapGenerated: false,
      mindmapRegenarated: false,
      messages: [], 
      pdfData: null,
      mindmap: {
        nodes: [],
        edges: [],
        phaseData: {
          empathy: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
          define: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
          ideate: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
          prototype: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
          test: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
          communicate: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []}
        }
      },
      // Session-specific conversation state
      conversationPhase: 'welcome',
      multipleChoiceOptions: null,
      multiselectOptions: null,
      selectedOptions: [],
      useCase: '',
      useCaseConfirmed: false,
      llmContext: '',
      selectedAbilities: [],
      selectedAbility: '',
      currentFrameworkPhase: 'Empathy',
      nextPhase: 'Define',
      selectedProjectTypes: null,
      isWaitingForResponse: false
    }
  ]);
  const [activeSessionId, setActiveSessionId] = useState(1);


  const activeSession = sessions.find(session => session.id === activeSessionId) || sessions[0];

  const updateSessionState = (sessionId, updates) => {
    setSessions(prevSessions => prevSessions.map(session => {
      if (session.id === sessionId) {
        return { ...session, ...updates };
      }
      return session;
    }));
  };


  const [selectedProjectTypes, setSelectedProjectTypes] = useState(activeSession?.selectedProjectTypes || null);


  const [selectedAbilities, setSelectedAbilities] = useState(activeSession?.selectedAbilities || []);
  const [selectedAbility, setSelectedAbility] = useState(activeSession?.selectedAbility || '');
  const [phase, setPhase] = useState('Empathy');
  const [nextPhase, setNextPhase] = useState(activeSession?.nextPhase || 'Define');
  
  // New state for use case context
  const [useCase, setUseCase] = useState(activeSession?.useCase || '');
  const [useCaseConfirmed, setUseCaseConfirmed] = useState(false);
  const [llmContext, setLlmContext] = useState(''); // The context interpreted by LLM





  
  const [viewMode, setViewMode] = useState('chat'); // 'chat' or 'mindmap'
  const [inputValue, setInputValue] = useState('Start');
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [showNewSessionInput, setShowNewSessionInput] = useState(false);
  const messagesEndRef = useRef(null);
  
  // New Diitrium conversation states
  const [conversationPhase, setConversationPhase] = useState(activeSession?.conversationPhase || 'welcome');
  const [multipleChoiceOptions, setMultipleChoiceOptions] = useState(activeSession?.multipleChoiceOptions || null);
  const [multiselectOptions, setMultiselectOptions] = useState(activeSession?.multiselectOptions || null);
  const [selectedOptions, setSelectedOptions] = useState(activeSession?.selectedOptions || []); // Track selected options for multiselect
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(activeSession?.isWaitingForResponse || false);
  const [isDiitriumMode, setIsDiitriumMode] = useState(true); // Toggle between original chat and Diitrium guided mode

  // Get current active session

  // Define the framework phases
  const frameworkPhases = ["Empathy", "Define", "Ideate", "Prototype", "Test", "Communicate"];
  const [currentFrameworkPhase, setCurrentFrameworkPhase] = useState(activeSession?.currentFrameworkPhase || 'Empathy');

  const mindmapRef = useRef(null);

  // Determine if send button should be enabled
  const isSendButtonEnabled = () => {
    const allowedPhases = ['welcome', 'use_case_input', 'use_case_confirmation', 'free_conversation'];
    return allowedPhases.includes(conversationPhase);
  };

  const handleDownloadPDF = async () => {
    const button = document.getElementById('pdf-download-btn');
    if (button) {
      button.textContent = 'Generating PDF...';
      button.disabled = true;
    }

    try {
      const result = await generatePDF({
        nodes: activeSession?.mindmap?.nodes || [],
        edges: activeSession?.mindmap?.edges || [],
        phaseData: activeSession?.mindmap?.phaseData || {},
        frameworkPhases: frameworkPhases,
        sessionName: activeSession?.title || 'Mindmap Export'
      });

      if (!result.success) {
        alert('Failed to generate PDF: ' + result.error);
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      if (button) {
        button.textContent = 'Download PDF';
        button.disabled = false;
      }
    }
  };
  // Auto scroll to bottom when new messages appear
  useEffect(() => {

    console.log('Active session messages updated:', activeSession.messages);
    console.log('Active session length:', activeSession.messages.length);
    console.log('Active session: ', activeSession);
    if (viewMode === 'chat') {
      scrollToBottom();
    }
  }, [activeSession.messages, viewMode]);

  // Initialize Diitrium welcome message
  useEffect(() => {
    if (isDiitriumMode && activeSession.messages.length === 0) {
      setSelectedProjectTypes(null);
      const updatedSessions = sessions.map(session => {
        if (session.id === activeSessionId) {
          return {
            ...session,
            messages: [...session.messages, {
              text: "Welcome to Dittrium, where we will explore the competencies of your project! I am Dittrium AI. Go ahead and type \"Start\" to begin your journey!",
              sender: 'bot'
            }]
          };
        }
        return session;
      });
      
      setSessions(updatedSessions);
    }
    
    // Sync local state with active session state
    if (activeSession) {
      setMultipleChoiceOptions(activeSession.multipleChoiceOptions || null);
      setMultiselectOptions(activeSession.multiselectOptions || null);
      setSelectedOptions(activeSession.selectedOptions || []);
      setConversationPhase(activeSession.conversationPhase || 'welcome');
      setUseCase(activeSession.useCase || '');
      setUseCaseConfirmed(activeSession.useCaseConfirmed || false);
      setLlmContext(activeSession.llmContext || '');
      setSelectedAbilities(activeSession.selectedAbilities || []);
      setSelectedAbility(activeSession.selectedAbility || '');
      setCurrentFrameworkPhase(activeSession.currentFrameworkPhase || 'Empathy');
      setNextPhase(activeSession.nextPhase || 'Define');
      setSelectedProjectTypes(activeSession.selectedProjectTypes || null);
      setIsWaitingForResponse(activeSession.isWaitingForResponse || false);
    }
  }, [activeSessionId, isDiitriumMode, activeSession]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  //updateMindmap(activeSessionId,currentFrameworkPhase, 'knowledge', selectedOptions);
  const updateMindmap = (activeSessionId, phase, category, selections, ability, useCase = '', currentFrameworkPhase = '') => {

    console.log(`Updating mindmap for session ${activeSessionId}, phase: ${phase}, category: ${category}, selections:`, selections, 'ability:', ability, 'useCase:', useCase, 'currentFrameworkPhase:', currentFrameworkPhase);
    
    setSessions(prevSessions => prevSessions.map(session => {

      if (session.id === activeSessionId) {
        const updatedMindmap = { ...session.mindmap };
        const phaseKey = phase.toLowerCase();
        
        // Initialize phase data if it doesn't exist
        if (!updatedMindmap.phaseData) {

          updatedMindmap.phaseData = {
            empathy: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
            define: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
            ideate: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
            prototype: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
            test: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []},
            communicate: {abilities: [], skills: [], knowledge: [], mindset: [], tools: []}
          };
        }
        
        if (!updatedMindmap.phaseData[phaseKey]) {
          updatedMindmap.phaseData[phaseKey] = {abilities: [], skills: [], knowledge: [], mindset: [], tools: []};
        }

        // Update the specific category with selections
        if (category === 'abilities') {
          updatedMindmap.phaseData[phaseKey].abilities = selections.filter(s => s !== "No metrics required for this phase");
   
        } else if (ability) {
          // For skills, knowledge, mindset, tools - associate with specific ability
          const abilityIndex = updatedMindmap.phaseData[phaseKey].abilities.indexOf(ability);
          if (abilityIndex >= 0) {
            if (!updatedMindmap.phaseData[phaseKey][`${ability}_${category}`]) {
              updatedMindmap.phaseData[phaseKey][`${ability}_${category}`] = [];
            }
            updatedMindmap.phaseData[phaseKey][`${ability}_${category}`] = selections.filter(s => s !== "No metrics required for this phase");
          }
    
        }

        // Generate nodes and edges from the updated data
        const { nodes, edges } = generateMindmapStructure(updatedMindmap.phaseData, frameworkPhases, useCase, currentFrameworkPhase);
        
        updatedMindmap.nodes = nodes;
        updatedMindmap.edges = edges;

        console.log('Updated mindmap structure:', updatedMindmap);

        return {
          ...session,
          mindmap: updatedMindmap
        };
      }
      return session;
    }));
  };
 
  // Create new session
  const handleCreateSession = () => {
    if (newSessionTitle.trim() === '') return;
    
    const newSession = {
      id: Date.now(),
      mindmapGenerated: false,
      mindmapRegenarated: false,
      pdfData: null,
      title: newSessionTitle,
      messages: [],
      mindmap: { nodes: [], edges: [] }
    };
    
    setSessions([...sessions, newSession]);
    setActiveSessionId(newSession.id);
    setNewSessionTitle('');
    setShowNewSessionInput(false);
    setViewMode('chat');
    
    // Reset conversation phase and use case for new session
    setConversationPhase('welcome');
    setMultipleChoiceOptions(null);
    setMultiselectOptions(null);
    setSelectedOptions([]);
    setUseCase('');
    setUseCaseConfirmed(false);
    setLlmContext('');
  };
  // Delete session
  const handleDeleteSession = (sessionId) => {
    const updatedSessions = sessions.filter(session => session.id !== sessionId);
    
    if (updatedSessions.length === 0) {
      // Create a new default session if all are deleted
      const defaultSession = { 
        id: Date.now(), 
        title: "New conversation", 
        messages: [],
        mindmap: { nodes: [], edges: [] } ,
        mindmapGenerated: false,
        mindmapRegenarated: false,
        pdfData: null

      };
      setSessions([defaultSession]);
      setActiveSessionId(defaultSession.id);
    } else {
      setSessions(updatedSessions);
      if (sessionId === activeSessionId) {
        setActiveSessionId(updatedSessions[0].id);
      }
    }
    
    // Reset conversation phase when deleting session
    setConversationPhase('welcome');
    setMultipleChoiceOptions(null);
    setMultiselectOptions(null);
    setSelectedOptions([]);
    setUseCase('');
    setUseCaseConfirmed(false);
    setLlmContext('');
  };
  // Handle message submission
  // Handle message submission
const handleSubmit = () => {
  if (inputValue.trim() === '' || !isSendButtonEnabled()) return;

  // Add user message to the current session
  const updatedSessions = sessions.map(session => {
    if (session.id === activeSessionId) {
      return {
        ...session,
        messages: [...session.messages, { text: inputValue, sender: 'user' }]
      };
    }
    return session;
  });
  
  setSessions(updatedSessions);
  
  // Process the message
  if (isDiitriumMode) {
    processDiitriumMessage(inputValue, conversationPhase);
  } else {
    // Original mindmap chat functionality
    processMessage(inputValue, activeSessionId);
  }
  
  setInputValue('');
};
  // Handle keyboard Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };
  // Toggle option selection for multiselect
  const toggleOptionSelection = (option) => {
    const currentSelectedOptions = activeSession.selectedOptions || [];
    let newSelectedOptions;
    
    if (currentSelectedOptions.includes(option)) {
      newSelectedOptions = currentSelectedOptions.filter(item => item !== option);
    } else {
      newSelectedOptions = [...currentSelectedOptions, option];
    }
    
    updateSessionState(activeSessionId, { selectedOptions: newSelectedOptions });
  };


  useEffect(() => {

  }, [selectedAbilities, selectedAbility, conversationPhase]);

  // Submit selected options
  const handleMultiselectSubmit = () => {
    if (selectedOptions.length === 0) return;

    // Check if the "No metrics" option was selected
    if (selectedOptions.length === 1 && selectedOptions[0] === "No metrics required for this phase") {
      // Add user's selection as a message
      const updatedSessions = sessions.map(session => {
        if (session.id === activeSessionId) {
          return {
            ...session,
            messages: [...session.messages, { text: `Selected: ${selectedOptions[0]}`, sender: 'user' }]
          };
        }
        return session;
      });
      setSessions(updatedSessions);
      
      // Skip this phase
      processDiitriumMessage("skip_phase", conversationPhase);
      
      // Clear selections
      updateSessionState(activeSessionId, { selectedOptions: [] });
      return;
    }

    // Regular multiselect handling
    const combinedMessage = selectedOptions.join(', ');
    
    // Add user's selections as a message
    const updatedSessions = sessions.map(session => {
      if (session.id === activeSessionId) {
        return {
          ...session,
          messages: [...session.messages, { text: `Selected: ${combinedMessage}`, sender: 'user' }]
        };
      }
      return session;
    });
    setSessions(updatedSessions);
    
    // Process the selections based on the current phase
    processDiitriumMessage(combinedMessage, conversationPhase);
    
    // Clear selections
    updateSessionState(activeSessionId, { selectedOptions: [] });
  };

  // Process Diitrium conversation flow
  const processDiitriumMessage = (message, conversationPhase) => {
    const generateAIResponse = (phase, additionalData = {}, callback) => {
      const basePayload = {
        LLMContext: llmContext,
        conversationPhase: phase,
        currentFrameworkPhase: currentFrameworkPhase,
        ...additionalData
      };

      fetch('https://us-central1-diitrium.cloudfunctions.net/api/process-question', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(basePayload),
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(result => {
        
        // Call the callback with the result
        if (callback) {
          callback(result, null);
        }
      })
      .catch(error => {
        console.error('Error generating AI response:', error);
        
        // Call callback with error
        if (callback) {
          callback(null, error);
        }
      });
    };

    // Function to get fallback messages
    const getFallbackMessage = (phase, additionalData) => {
      switch (phase) {
        case 'abilities_selection':
          return {
            text: `Great! Let's start by selecting the abilities you want to explore for your project. Please select all that apply:`,
            abilitiesWithSkipOption: [...data[currentFrameworkPhase].abilities, "No metrics required for this phase"]
          };
        case 'specific_question':
          return {
            text: `Perfect! You've selected some great abilities to focus on. What specific questions do you have about these competencies and how they apply to your project?`
          };
        case 'question_response':
          return {
            text: `Thank you for your question. Here's some guidance based on your project context and selected abilities.`
          };
        case 'continue_or_explore':
          return {
            text: `Great progress! Would you like to continue exploring these abilities or move on to a different area?`
          };
        case 'phase_transition':
          return {
            text: `Excellent work on this phase! Let's move forward to explore the next set of competencies.`
          };
        default:
          return {
            text: `Let's continue with your design exploration.`
          };
      }
    };

    const lowerInput = message.toLowerCase().trim();

    updateSessionState(activeSessionId, { isWaitingForResponse: true });

    
    setIsWaitingForResponse(true);
    
    setTimeout(() => {
      let botResponse = { text: "", sender: 'bot' };
      
      // Handle conversation based on current phase
      switch(conversationPhase) {
        case 'welcome':
          if (lowerInput === 'start') {
            updateSessionState(activeSessionId, {
              conversationPhase: 'use_case_input',
              multipleChoiceOptions: null,
              multiselectOptions: null
            });


            setConversationPhase('use_case_input');
            
            botResponse.text = "Before we begin, could you please describe your use case or design challenge? This will help me understand the context of your session better."; 
            setMultipleChoiceOptions(null);
            setMultiselectOptions(null);
          } else {
            botResponse.text = "To begin, please type \"Start\".";
          }
          break;

        case 'use_case_input':

            updateSessionState(activeSessionId, { useCase: message });
            // Store the use case input
            setUseCase(message);
            
            // Show loading state
            // Make API call to the backend to process the use case using Google's Gemini LLM
            fetch('https://us-central1-diitrium.cloudfunctions.net/api/process-use-case', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ useCase: message })
            })
            .then(response => response.json())
            .then(data => {

              updateSessionState(activeSessionId, { llmContext: data.processedUseCase });
              // Store the context from the LLM
              setLlmContext(data.processedUseCase);
          
              // Fix: Properly check if the response is flagged as inappropriate
              if (data.processedUseCase === "inappropriate" || data.processedUseCase === "Inappropriate") {
                // Handle inappropriate use case
                const botResponseText = "It seems that the use case you provided is not relevant to design. Could you please provide a different use case or design challenge?";
                
                // Directly update the sessions state with the bot response
                setSessions(prevSessions => prevSessions.map(session => {
                  if (session.id === activeSessionId) {
                    const updatedMessages = [...session.messages];
                
                    return {
                      ...session,
                      messages: [
                        ...updatedMessages,
                        {
                          text: botResponseText,
                          sender: 'bot'
                        }
                      ]
                    };
                  }
                  return session;
                }));
                
                
                
              } else {
                // Move to confirmation phase with valid use case
                setConversationPhase('use_case_confirmation');

                updateSessionState(activeSessionId, {
                conversationPhase: 'use_case_confirmation',
                multipleChoiceOptions: ["Yes, that's correct", "No, let me explain again"]
              });

                
                const botResponseText = `I understand your use case as: ${data.processedUseCase}\n\nIs this correct?`;
                
                // Directly update the sessions state with the bot response
                setSessions(prevSessions => prevSessions.map(session => {
                  if (session.id === activeSessionId) {
                    const updatedMessages = [...session.messages];
                    
                
                    return {
                      ...session,
                      messages: [
                        ...updatedMessages,
                        {
                          text: botResponseText,
                          sender: 'bot'
                        }
                      ]
                    };
                  }
                  return session;
                }));
                
                
                setMultipleChoiceOptions(["Yes, that's correct", "No, let me explain again"]);
              }
            })
            .catch(error => {
              console.error('Error processing use case:', error);
              
              // Directly update the sessions state with the error message
              setSessions(prevSessions => prevSessions.map(session => {
                if (session.id === activeSessionId) {
                  return {
                    ...session,
                    messages: [...session.messages, { 
                      text: "I'm having trouble understanding your use case. Could you try explaining it differently?", 
                      sender: 'bot' 
                    }]
                  };
                }
                return session;
              }));
            })
            .finally(() => {
              updateSessionState(activeSessionId, { isWaitingForResponse: false });
              setIsWaitingForResponse(false);
            });
            break;

        case 'use_case_confirmation':
              if (lowerInput.includes('yes')) {

                console.log('Current Framework Phase before abilities:', currentFrameworkPhase); // ADD THIS
                console.log('Data keys:', Object.keys(data)); // ADD THIS
                
                setUseCaseConfirmed(true);
                setMultipleChoiceOptions(null);
                setConversationPhase('abilities_selection');

                updateSessionState(activeSessionId, {
                  useCaseConfirmed: true,
                  multipleChoiceOptions: null,
                  conversationPhase: 'abilities_selection'
                });
                
                botResponse.text = "Perfect! Let me prepare the abilities selection for your project...";
                
                // Generate AI response for abilities selection
                generateAIResponse('abilities_selection', {
                  abilities: data[currentFrameworkPhase].abilities
                }, (result, error) => {
                  if (result) {
                    botResponse.text = result.botResponseText;
                    if (result.abilitiesWithSkipOption) {
                      updateSessionState(activeSessionId, { multiselectOptions: result.abilitiesWithSkipOption });

                      setMultiselectOptions(result.abilitiesWithSkipOption);
                    }
                  } else {
                    const fallback = getFallbackMessage('abilities_selection');
                    updateSessionState(activeSessionId, { multiselectOptions: fallback.abilitiesWithSkipOption });

                    botResponse.text = fallback.text;
                    setMultiselectOptions(fallback.abilitiesWithSkipOption);
                  }
                });
              } else {
                setConversationPhase('use_case_input');
                updateSessionState(activeSessionId, {
                  conversationPhase: 'use_case_input',
                  multipleChoiceOptions: null
                });
                botResponse.text = "I apologize for misunderstanding. Please describe your use case or design challenge again, with more details if possible:";
                setMultipleChoiceOptions(null);
              }
              break;

        case 'abilities_selection':
          if (message === "skip_phase") {
            let newNextPhase;
            switch (currentFrameworkPhase) {
              case 'Empathy': newNextPhase = 'Define'; break;
              case 'Define': newNextPhase = 'Ideate'; break;
              case 'Ideate': newNextPhase = 'Prototype'; break;
              case 'Prototype': newNextPhase = 'Test'; break;
              case 'Test': newNextPhase = 'Communicate'; break;
              case 'Communicate': newNextPhase = 'completed'; break;
              default: newNextPhase = 'Define';
            }

                if (nextPhase === 'completed') {
                      botResponse.text = `You've chosen to skip the ${currentFrameworkPhase} phase. Congratulations! You've completed your design thinking journey.`;
                      setConversationPhase('completed');
                      updateSessionState(activeSessionId, {
                        conversationPhase: 'completed',
                        multipleChoiceOptions: null,
                        multiselectOptions: null
                      });
                } else {
                  botResponse.text = `You've chosen to skip the ${currentFrameworkPhase} phase. Let's move on to the ${nextPhase} phase.`;
                  updateSessionState(activeSessionId, {
                     currentFrameworkPhase: newNextPhase
                  });
                  
                  setPhase(nextPhase);
                  setCurrentFrameworkPhase(nextPhase);

                  if (nextPhase === 'Ideate' && !selectedProjectTypes) {
                    // Show project type selection for Ideate phase
                    setConversationPhase('project_type_selection');

                    updateSessionState(activeSessionId, {
                      conversationPhase: 'project_type_selection'
                    });
                    
                    setTimeout(() => {
                      setSessions(prevSessions => prevSessions.map(session => {
                        if (session.id === activeSessionId) {
                          return {
                            ...session,
                            messages: [...session.messages, { 
                              text: "To provide you with the most relevant design abilities, please tell me what your project involves. You can select multiple options:", 
                              sender: 'bot' 
                            }]
                          };
                        }
                        return session;
                      }));
                      
                      setMultiselectOptions([
                        "Educational Service",
                        "Software Development", 
                        "Physical Engineering",
                        "AI Integration"
                      ]);
                    }, 100);
                  } else {
                    updateSessionState(activeSessionId, {
                      conversationPhase: 'abilities_selection'
                    });
                    setConversationPhase('abilities_selection');
                    
                    // Generate AI response for abilities selection
                    setTimeout(() => {
                      generateAIResponse('abilities_selection', {
                        abilities: data[nextPhase].abilities,
                        currentFrameworkPhase: nextPhase
                      }, (result, error) => {
                        let responseText, optionsToSet;
                        
                        if (result) {
                          responseText = result.botResponseText;
                          optionsToSet = result.abilitiesWithSkipOption;
                        } else {
                          const fallback = getFallbackMessage('abilities_selection');
                          responseText = fallback.text;
                          optionsToSet = fallback.abilitiesWithSkipOption || [...data[nextPhase].abilities, "No metrics required for this phase"];
                        }
                        
                        setSessions(prevSessions => prevSessions.map(session => {
                          if (session.id === activeSessionId) {
                            return {
                              ...session,
                              messages: [...session.messages, { 
                                text: responseText, 
                                sender: 'bot' 
                              }]
                            };
                          }
                          return session;
                        }));
                        
                        setMultiselectOptions(optionsToSet);
                      });
                    }, 100);
                  }
                }

                updateSessionState(activeSessionId, {
              multipleChoiceOptions: null,
              multiselectOptions: [...data[newNextPhase].abilities, "No metrics required for this phase"]

            });
                
                setMultipleChoiceOptions(null);
                setMultiselectOptions([...data[newNextPhase].abilities, "No metrics required for this phase"]);

                break; // Important: break here to prevent further execution
              }
                // Check if we're in the IDEATE phase and need to ask project type first
                
              
                // If we have selected options from abilities_selection, process them
                updateMindmap(activeSessionId, currentFrameworkPhase, 'abilities', selectedOptions, null, useCase);
                updateSessionState(activeSessionId, {
            selectedAbilities: selectedOptions,
            selectedAbility: selectedOptions[0]
          });

                setSelectedAbilities(selectedOptions);
                setSelectedAbility(selectedOptions[0]);
                
                // Find the index of the selected ability in the current phase's abilities array
                const abilityIndex = data[currentFrameworkPhase].abilities.indexOf(selectedOptions[0]);
                
                if (abilityIndex >= 0) {
                  botResponse.text = `Now let's explore skills related to "${selectedOptions[0]}". Please select all skills that you think are relevant:`;
                  
                  const skillsWithSkipOption = [...data[currentFrameworkPhase][abilityIndex].skills, "No metrics required for this phase"];
                  updateSessionState(activeSessionId, {
              multiselectOptions: skillsWithSkipOption,
              conversationPhase: 'skills_selection'
            });
                  setMultiselectOptions(skillsWithSkipOption);
                  setConversationPhase('skills_selection');
                } else {
                  botResponse.text = `I couldn't find the skills for that ability. Let's try selecting ${currentFrameworkPhase} abilities again.`;
                  setMultiselectOptions([...data[currentFrameworkPhase].abilities, "No metrics required for this phase"]);
                  updateSessionState(activeSessionId, {
              multiselectOptions: [...data[currentFrameworkPhase].abilities, "No metrics required for this phase"]
            });
                  
                  
                }
                break;

        case 'project_type_selection':
            updateSessionState(activeSessionId, { multipleChoiceOptions: null });

            setMultipleChoiceOptions(null);
            console.log('Project type selection phase');
                  // Map project types to ability indices
                  const projectTypeMapping = {
                    "Educational Service": [11, 12, 5, 6, 10, 7],
                    "Software Development": [11, 12, 2, 7],
                    "Physical Engineering": [11, 12, 0, 1, 3, 4],
                    "AI Integration": [11, 12, 7, 8, 9]
                  };
                  
                
                  // Get all unique ability indices based on selected project types
                  const selectedIndices = new Set();
                  selectedOptions.forEach(projectType => {
                    if (projectTypeMapping[projectType]) {
                      projectTypeMapping[projectType].forEach(index => selectedIndices.add(index));
                    }
                  });
                
                  // Convert indices to actual ability names from your data structure
                  const relevantAbilities = Array.from(selectedIndices).map(index => {
                    // Fix: Add bounds checking and handle potential undefined abilities
                    const abilities = data[currentFrameworkPhase]?.abilities;
                    if (abilities && index >= 1 && index <= abilities.length) {
                      return abilities[index - 1]; // Convert 1-based index to 0-based
                    }
                    return null;
                  }).filter(Boolean); // Remove any null/undefined values
                
                  // Store the selected project types for future reference
                  setSelectedProjectTypes(selectedOptions);

                  updateSessionState(activeSessionId, {
                    selectedProjectTypes: selectedOptions,
                    conversationPhase: 'abilities_selection',
                    multiselectOptions: [...relevantAbilities, "No metrics required for this phase"]
                  });
                
                  botResponse.text = `Based on your project type(s), here are the most relevant ${currentFrameworkPhase} abilities. Please select the ones you want to focus on:`;
                  setMultiselectOptions([...relevantAbilities, "No metrics required for this phase"]);
                  setConversationPhase('abilities_selection'); // Fix: Changed from 'skills_selection' to 'abilities_selection'
                  break;

        case 'skills_selection':
            // Check if user wants to skip this phase
            console.log('Skills selection message:', message, typeof message);
            if (message === "skip_phase") {
              botResponse.text = `You've chosen to skip the skills for ${selectedAbility}. Let's move on to the knowledge areas.`;
              
              // Get the current framework phase data
              // Find the index of the current ability
              const abilityIdx = data[currentFrameworkPhase].abilities.indexOf(selectedAbility);
              
              if (abilityIdx >= 0) {
                botResponse.text = `Let's explore the knowledge areas needed for ${selectedAbility}:`;
                setMultiselectOptions([...data[currentFrameworkPhase][abilityIdx].knowledge, "No metrics required for this phase"]);
                setConversationPhase('knowledge_selection');
                updateSessionState(activeSessionId, {
                multiselectOptions: [...data[currentFrameworkPhase][abilityIdx].knowledge, "No metrics required for this phase"],
                conversationPhase: 'knowledge_selection'
              });


              } else {
                botResponse.text = `Let's move on to the next part of our discussion about your ${currentFrameworkPhase} phase.`;
                updateSessionState(activeSessionId, {
                conversationPhase: 'free_conversation',
                multiselectOptions: null
              });


                setConversationPhase('free_conversation');
                setMultiselectOptions(null);
              }
              break;
            }

            updateMindmap(activeSessionId,currentFrameworkPhase, 'skills', selectedOptions, selectedAbility);
            
            // Get the current framework phase data
            // Find the index of the current ability we're working with
            const currentAbilityIndex = data[currentFrameworkPhase].abilities.indexOf(selectedAbility);
            
            if (currentAbilityIndex >= 0) {
              botResponse.text = `Great selection of skills for ${selectedAbility}! Now let's explore the knowledge areas associated with these skills:`;
              
              // Add the option to skip knowledge areas
              const knowledgeWithSkipOption = [...data[currentFrameworkPhase][currentAbilityIndex].knowledge, "No metrics required for this phase"];
              updateSessionState(activeSessionId, {
              multiselectOptions: knowledgeWithSkipOption,
              conversationPhase: 'knowledge_selection'
            });

              setMultiselectOptions(knowledgeWithSkipOption);
              setConversationPhase('knowledge_selection');
            } else {
              botResponse.text = `Let's continue exploring the skills needed for your ${currentFrameworkPhase} phase. Could you tell me more about your design challenge?`;
              updateSessionState(activeSessionId, {
              multipleChoiceOptions: null,
              multiselectOptions: null,
              conversationPhase: 'free_conversation'
            });

              setMultipleChoiceOptions(null);
              setMultiselectOptions(null);
              setConversationPhase('free_conversation');
            }
            break;
 
        case 'knowledge_selection':
            // Check if user wants to skip this phase
            if (message === "skip_phase") {
              botResponse.text = `You've chosen to skip the knowledge areas for ${selectedAbility}. Let's move on to the mindset.`;
              
              
              // Get the current framework phase data
              
              // Find the index of the current ability
              const abilityIdx = data[currentFrameworkPhase].abilities.indexOf(selectedAbility);

              
              if (abilityIdx >= 0) {
                botResponse.text = `Let's explore the mindset needed for ${selectedAbility}:`;
                updateSessionState(activeSessionId, {
                multiselectOptions: [...data[currentFrameworkPhase][abilityIdx].mindset, "No metrics required for this phase"],
                conversationPhase: 'mindset_selection'
              });

                setMultiselectOptions([...data[currentFrameworkPhase][abilityIdx].mindset, "No metrics required for this phase"]);
                setConversationPhase('mindset_selection');
              } else {
                botResponse.text = `Let's move on to the next part of our discussion about your ${currentFrameworkPhase} phase.`;
                setConversationPhase('free_conversation');
                setMultiselectOptions(null);
                updateSessionState(activeSessionId, {
                conversationPhase: 'free_conversation',
                multiselectOptions: null
              });
              }
              break;
            }

            updateMindmap(activeSessionId,currentFrameworkPhase, 'knowledge', selectedOptions, selectedAbility);
            
    
            // Get the current framework phase data
            const currentPhaseKnowledgeData = data[currentFrameworkPhase];              
            // Find the index of the current ability
            const abilityIdx = currentPhaseKnowledgeData.abilities.indexOf(selectedAbility);
            
            if (abilityIdx >= 0) {
              botResponse.text = `Excellent! Now let's explore the mindset needed for ${selectedAbility}:`;
              
              // Add the option to skip mindset
              const mindsetWithSkipOption = [...currentPhaseKnowledgeData[abilityIdx].mindset, "No metrics required for this phase"];
              setMultiselectOptions(mindsetWithSkipOption);
              setConversationPhase('mindset_selection');
              updateSessionState(activeSessionId, {
              multiselectOptions: mindsetWithSkipOption,
              conversationPhase: 'mindset_selection'
            });
            } else {
              botResponse.text = `Great selections! Let's move on to discussing the instruments you might need for your ${currentFrameworkPhase} phase.`;
              setConversationPhase('free_conversation');
              setMultiselectOptions(null);
              updateSessionState(activeSessionId, {
              conversationPhase: 'free_conversation',
              multiselectOptions: null
            });
            }
            break;
            
        case 'mindset_selection':
            // Check if user wants to skip this phase
            if (message === "skip_phase") {
              botResponse.text = `You've chosen to skip the mindset for ${selectedAbility}. Let's move on to the instruments.`;
              
              
              // Get the current framework phase data
              
              // Find the index of the current ability
              const aIndex = data[currentFrameworkPhase].abilities.indexOf(selectedAbility);
              
              if (aIndex >= 0) {
                botResponse.text = `Let's look at some instruments that can help with ${selectedAbility}:`;
                setMultiselectOptions([...data[currentFrameworkPhase][aIndex].tools, "No metrics required for this phase"]);
                setConversationPhase('tools_selection');
                updateSessionState(activeSessionId, {
                multiselectOptions: [...data[currentFrameworkPhase][aIndex].tools, "No metrics required for this phase"],
                conversationPhase: 'tools_selection'
              });
              } else {
                botResponse.text = `Let's move on to the next part of our discussion about your ${currentFrameworkPhase} phase.`;
                setConversationPhase('free_conversation');
                setMultiselectOptions(null);
                updateSessionState(activeSessionId, {
                conversationPhase: 'free_conversation',
                multiselectOptions: null
              });
              }
              break;
            }
            
            updateMindmap(activeSessionId,currentFrameworkPhase, 'mindset', selectedOptions, selectedAbility);
       
            // Get the current framework phase data
            
            // Find the index of the current ability
            const aIndex = data[currentFrameworkPhase].abilities.indexOf(selectedAbility);
            
            if (aIndex >= 0) {
              botResponse.text = `Finally, let's look at some instruments that can help with ${selectedAbility}:`;
              
              // Add the option to skip tools
              const toolsWithSkipOption = [...data[currentFrameworkPhase][aIndex].tools, "No metrics required for this phase"];
              setMultiselectOptions(toolsWithSkipOption);
              setConversationPhase('tools_selection');
              updateSessionState(activeSessionId, {
              multiselectOptions: toolsWithSkipOption,
              conversationPhase: 'tools_selection'
            });
            } else {
              botResponse.text = `Great selections! Now let's move on to the next ability or ${currentFrameworkPhase} phase element.`;
              updateSessionState(activeSessionId, {
              conversationPhase: 'free_conversation',
              multiselectOptions: null
            });
              setConversationPhase('free_conversation');
              setMultiselectOptions(null);
            }
            break;
            
        case 'tools_selection':
              // Check if user wants to skip this phase
              if (message === "skip_phase") {
                botResponse.text = `You've chosen to skip the instruments for ${selectedAbility}.`;
                
                // Check if we have more abilities to explore
                const remainingAbilities = selectedAbilities.slice(1);
                
                if (remainingAbilities.length > 0) {
                  // Move to the next ability
                  setSelectedAbility(remainingAbilities[0]);
                  setSelectedAbilities(remainingAbilities);
                  
                  // Get the current framework phase data
                  
                  // Find the index of the next ability
                  const nextAbilityIndex = data[currentFrameworkPhase].abilities.indexOf(remainingAbilities[0]);

                  updateSessionState(activeSessionId, {
                selectedAbility: remainingAbilities[0],
                selectedAbilities: remainingAbilities
              });
                  
                  if (nextAbilityIndex >= 0) {
                    botResponse.text = `Let's move on to the next ability: "${remainingAbilities[0]}". Please select relevant skills:`;
                    updateSessionState(activeSessionId, {
                  multiselectOptions: [...data[currentFrameworkPhase][nextAbilityIndex].skills, "No metrics required for this phase"],
                  conversationPhase: 'skills_selection'
                });
                    setMultiselectOptions([...data[currentFrameworkPhase][nextAbilityIndex].skills, "No metrics required for this phase"]);
                    setConversationPhase('skills_selection');
                  } else {
                    botResponse.text = `I couldn't find information about the next ability. Let's discuss your ${currentFrameworkPhase} phase further.`;
                    setConversationPhase('free_conversation');
                    setMultiselectOptions(null);
                    updateSessionState(activeSessionId, {
                  conversationPhase: 'free_conversation',
                  multiselectOptions: null
                });
                  }
                } else {
                  // No more abilities to explore - move to next phase
                  let newNextPhase;
                switch (currentFrameworkPhase) {
                  case 'Empathy': newNextPhase = 'Define'; break;
                  case 'Define': newNextPhase = 'Ideate'; break;
                  case 'Ideate': newNextPhase = 'Prototype'; break;
                  case 'Prototype': newNextPhase = 'Test'; break;
                  case 'Test': newNextPhase = 'Communicate'; break;
                  case 'Communicate': newNextPhase = 'completed'; break;
                  default: newNextPhase = 'Define';
                }
                  
                  if (newNextPhase === 'completed') {
                    botResponse.text = `Excellent! We've completed all phases of your design thinking journey. Your project is now fully explored through all stages: empathy, define, ideate, prototype, test, and communicate.`;
                    updateSessionState(activeSessionId, {
                      conversationPhase: 'completed',
                      multiselectOptions: null,
                      multipleChoiceOptions: null
                    });
                    
                  } else {
                    botResponse.text = `Great! We've completed the ${currentFrameworkPhase} phase. Let's move on to the ${nextPhase} phase.`;
                    
                    
                    updateSessionState(activeSessionId, {
                      currentFrameworkPhase: newNextPhase,
                      conversationPhase: 'abilities_selection',
                      nextPhase: newNextPhase
                    });
                    // Generate AI response for the new phase abilities selection
                    generateAIResponse('abilities_selection', {
                  abilities: data[newNextPhase].abilities
                }, (result, error) => {
                  if (result) {
                    botResponse.text = `${botResponse.text}\n\n${result.botResponseText}`;
                    if (result.abilitiesWithSkipOption) {
                      updateSessionState(activeSessionId, { multiselectOptions: result.abilitiesWithSkipOption });
                    }
                  } else {
                    const fallback = getFallbackMessage('abilities_selection');
                    botResponse.text = `${botResponse.text}\n\n${fallback.text}`;
                    updateSessionState(activeSessionId, {
                      multiselectOptions: fallback.abilitiesWithSkipOption || [...data[newNextPhase].abilities, "No metrics required for this phase"]
                    });
                  }
                });
              }
            }
                break;
              }
              
              updateMindmap(activeSessionId,currentFrameworkPhase, 'tools', selectedOptions, selectedAbility);
          
              // Check if we have more abilities to explore
              const remainingAbilities = selectedAbilities.slice(1);
              
              if (remainingAbilities.length > 0) {
            const nextAbilityIndex = data[currentFrameworkPhase].abilities.indexOf(remainingAbilities[0]);
            
            updateSessionState(activeSessionId, {
              selectedAbility: remainingAbilities[0],
              selectedAbilities: remainingAbilities
            });
            
            if (nextAbilityIndex >= 0) {
              botResponse.text = `Let's move on to the next ability: "${remainingAbilities[0]}". Please select relevant skills:`;
              updateSessionState(activeSessionId, {
                multiselectOptions: [...data[currentFrameworkPhase][nextAbilityIndex].skills, "No metrics required for this phase"],
                conversationPhase: 'skills_selection'
              });
            } else {
              botResponse.text = `I couldn't find information about the next ability. Let's discuss your ${currentFrameworkPhase} project further.`;
              updateSessionState(activeSessionId, {
                conversationPhase: 'free_conversation',
                multiselectOptions: null
              });
            }
          } else {
            let newNextPhase;
            switch (currentFrameworkPhase) {
              case 'Empathy': newNextPhase = 'Define'; break;
              case 'Define': newNextPhase = 'Ideate'; break;
              case 'Ideate': newNextPhase = 'Prototype'; break;
              case 'Prototype': newNextPhase = 'Test'; break;
              case 'Test': newNextPhase = 'Communicate'; break;
              case 'Communicate': newNextPhase = 'completed'; break;
              default: newNextPhase = 'Define';
            }
            
            if (newNextPhase === 'completed') {
              botResponse.text = `Excellent! We've completed all phases of your design thinking journey. Your project has been thoroughly explored through empathy, define, ideate, prototype, test, and communicate phases.`;
              updateSessionState(activeSessionId, {
                conversationPhase: 'completed',
                multiselectOptions: null,
                multipleChoiceOptions: null
              });
            } else {
              botResponse.text = `Great! We've explored all the abilities you selected for the ${currentFrameworkPhase} phase. Would you like to move on to the ${newNextPhase} phase?`;
              updateSessionState(activeSessionId, {
                multipleChoiceOptions: [`Yes, let's move to the ${newNextPhase} phase`],
                multiselectOptions: null,
                conversationPhase: 'phase_transition',
                nextPhase: newNextPhase
              });
            }
          }
          break;

        case 'phase_transition':
          if (lowerInput.includes('yes')) {
            const phaseToMoveTo = nextPhase || 'define';
            
            updateSessionState(activeSessionId, {
              currentFrameworkPhase: phaseToMoveTo
            });
            
            if (phaseToMoveTo === 'Ideate' && !selectedProjectTypes) {
              updateSessionState(activeSessionId, {
                multipleChoiceOptions: null,
                conversationPhase: 'project_type_selection',
                multiselectOptions: [
                  "Educational Service",
                  "Software Development", 
                  "Physical Engineering",
                  "AI Integration"
                ]
              });
              
              botResponse.text = `To provide you with the most relevant design abilities, please tell me what your project involves. You can select multiple options:`;
            } else {
              botResponse.text = `Generating abilities...`;
              
              updateSessionState(activeSessionId, {
                conversationPhase: 'abilities_selection',
                multipleChoiceOptions: null,
                multiselectOptions: null
              });

              let transitionNextPhase;
              switch (currentFrameworkPhase) {
                case 'Empathy': transitionNextPhase = 'Define'; break;
                case 'Define': transitionNextPhase = 'Ideate'; break;
                case 'Ideate': transitionNextPhase = 'Prototype'; break;
                case 'Prototype': transitionNextPhase = 'Test'; break;
                case 'Test': transitionNextPhase = 'Communicate'; break;
                case 'Communicate': transitionNextPhase = 'completed'; break;
                default: transitionNextPhase = 'Define';
              }
              
              generateAIResponse('abilities_selection', {
                abilities: data[phaseToMoveTo].abilities,
                currentFrameworkPhase: transitionNextPhase,
              }, (result, error) => {
                if (result) {
                  botResponse.text = `${result.botResponseText}`;
                  if (result.abilitiesWithSkipOption) {
                    updateSessionState(activeSessionId, { multiselectOptions: result.abilitiesWithSkipOption });
                  }
                } else {
                  const fallback = getFallbackMessage('abilities_selection');
                  botResponse.text = `${botResponse.text}\n\n${fallback.text}`;
                  updateSessionState(activeSessionId, {
                    multiselectOptions: fallback.abilitiesWithSkipOption || [...data[phaseToMoveTo].abilities, "No metrics required for this phase"]
                  });
                }
              });
            }
          } else {
            botResponse.text = "Sure, let's continue discussing your project. What aspects would you like to explore further?";
            updateSessionState(activeSessionId, {
              conversationPhase: 'free_conversation',
              multipleChoiceOptions: null,
              multiselectOptions: null
            });
          }
          break;


        case 'free_conversation':
                  default:
                    // Simulate LLM API response
                    const responses = [
                      "That's an interesting perspective. Could you elaborate on how that relates to your project goals?",
                      "I understand. Have you considered how this might impact your target users?",
                      "Great insight! How does this connect with the challenges you identified earlier?",
                      `Based on your use case of "${useCase}", how do you see this affecting your approach?`,
                      "I see what you mean. What specific aspects of this problem are most important to address?",
                      "Would you like to add this concept to your mind map? You can use '/mindmap' to view and edit your mind map."
                    ];
                    
                    botResponse.text = responses[Math.floor(Math.random() * responses.length)];
                    break;
                }
          
      // Add bot response to the current session
      if (botResponse.text !== "") {
        setSessions(prevSessions => prevSessions.map(session => {
          if (session.id === activeSessionId) {
            return {
              ...session,
              messages: [...session.messages, botResponse]
            };
          }
          return session;
        }));
      
        // Scroll to the bottom only if something was added
        scrollToBottom();
      }

      updateSessionState(activeSessionId, { isWaitingForResponse: false });

      
      setIsWaitingForResponse(false);
    }, 800);
  };
  // Handle multiple choice selection
  const handleOptionSelect = (option) => {
    // Add user message
    const updatedSessions = sessions.map(session => {
      if (session.id === activeSessionId) {
        return {
          ...session,
          messages: [...session.messages, { text: option, sender: 'user' }]
        };
      }
      return session;
    });
    
    setSessions(updatedSessions);
    
    // Process the selection
    processDiitriumMessage(option, conversationPhase);
  };
  // Process message for original mindmap chat functionality
  const processMessage = (message, sessionId) => {
    setTimeout(() => {
      let botResponse = {};
      let updatedMindmap = { ...activeSession.mindmap };
      
      if (message.trim().toLowerCase() === '/mindmap') {
        // Switch to mindmap view
        setViewMode('mindmap');
        botResponse = {
          text: "Switching to mindmap view. Here you can visualize our conversation.",
          sender: 'bot'
        };
      } 
      else if (message.trim().toLowerCase().startsWith('/add-node ')) {
        // Add node to mindmap
        const nodeName = message.substring(10).trim();
        if (nodeName) {
          // Add node to mindmap
          const newNode = {
            id: `node-${Date.now()}`,
            label: nodeName,
            x: Math.random() * 500,
            y: Math.random() * 300
          };
          
          // Connect to a central node if it exists, or create a central node
          let centralNodeId;
          if (updatedMindmap.nodes.length === 0) {
            const centralNode = {
              id: 'central-node',
              label: 'Main Topic',
              x: 250,
              y: 150
            };
            updatedMindmap.nodes = [centralNode];
            centralNodeId = 'central-node';
          } else {
            centralNodeId = updatedMindmap.nodes[0].id;
          }
          
          updatedMindmap.nodes.push(newNode);
          updatedMindmap.edges.push({
            id: `edge-${Date.now()}`,
            source: centralNodeId,
            target: newNode.id
          });
          
          botResponse = {
            text: `Added "${nodeName}" to the mindmap.`,
            sender: 'bot'
          };
        } else {
          botResponse = {
            text: "Please specify a node name. Example: /add-node Project Ideas",
            sender: 'bot'
          };
        }
      }
      else if (message.trim().toLowerCase().startsWith('/connect ')) {
        // Connect nodes in mindmap
        const params = message.substring(9).trim().split(' to ');
        if (params.length === 2) {
          const sourceLabel = params[0].trim();
          const targetLabel = params[1].trim();
          
          // Find nodes by label (simplified - in real app you'd want unique identifiers)
          const sourceNode = updatedMindmap.nodes.find(n => n.label.toLowerCase() === sourceLabel.toLowerCase());
          const targetNode = updatedMindmap.nodes.find(n => n.label.toLowerCase() === targetLabel.toLowerCase());
          
          if (sourceNode && targetNode) {
            updatedMindmap.edges.push({
              id: `edge-${Date.now()}`,
              source: sourceNode.id,
              target: targetNode.id
            });
            
            botResponse = {
              text: `Connected "${sourceLabel}" to "${targetLabel}" in the mindmap.`,
              sender: 'bot'
            };
          } else {
            botResponse = {
              text: "Couldn't find one or both nodes. Please check node names.",
              sender: 'bot'
            };
          }
        } else {
          botResponse = {
            text: "Please use format: /connect [Node1] to [Node2]",
            sender: 'bot'
          };
        }
      }
      else if (message.trim().toLowerCase() === '/help') {
        // Show available commands
        botResponse = {
          text: `Available commands:
                  /mindmap - Switch to mindmap view
                  /add-node [Name] - Add a new node to the mindmap
                  /connect [Node1] to [Node2] - Connect two existing nodes
                  /chat - Switch back to chat view
                  /help - Show this help message
                  /diitrium - Switch to Diitrium guided mode
                  /classic - Switch to classic chat mode`,
          sender: 'bot'
        };
      }
      else if (message.trim().toLowerCase() === '/chat') {
        // Switch to chat view
        setViewMode('chat');
        botResponse = {
          text: "Switching back to chat view.",
          sender: 'bot'
        };
      }
      else if (message.trim().toLowerCase() === '/diitrium') {
        // Switch to Diitrium guided mode
        setIsDiitriumMode(true);
        setConversationPhase('welcome');
        setUseCase('');
        setUseCaseConfirmed(false);

        updateSessionState(activeSessionId, {
          conversationPhase: 'welcome',
          useCase: '',
          useCaseConfirmed: false
        });


        botResponse = {
          text: "Switching to Dittrium guided mode. Type 'Start' to begin.",
          sender: 'bot'
        };
      }
      else if (message.trim().toLowerCase() === '/classic') {
        // Switch to classic chat mode
        setIsDiitriumMode(false);
        botResponse = {
          text: "Switching to classic chat mode. Type /help to see available commands.",
          sender: 'bot'
        };
      }
      else {
        // Normal chatbot response
        botResponse = {
          text: `I received your message: "${message}". Type /help to see available mindmap commands.`,
          sender: 'bot'
        };
        
        // Attempt to extract concepts for mindmap (simplified)
        const words = message.split(' ');
        const nouns = words.filter(word => word.length > 4).slice(0, 2); // Very simplified concept extraction
        
        if (nouns.length > 0 && updatedMindmap.nodes.length > 0) {
          const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
          // Sometimes suggest a node
          if (Math.random() > 0.5) {
            botResponse.text += ` Would you like to add "${randomNoun}" to your mindmap? Try "/add-node ${randomNoun}"`;
          }
        }
      }
      
      // Update session with bot response and mindmap
      const finalUpdatedSessions = sessions.map(session => {
        if (session.id === sessionId) {
          return {
            ...session,
            messages: [...session.messages, botResponse],
            mindmap: updatedMindmap
          };
        }
        return session;
      });
      
      setSessions(finalUpdatedSessions);
    }, 500); // Simulate delay for response
  };
  // Render mindmap view (simplified)
  const renderMindmap = (session, activeSessionId, sessions, setSessions) => {
    const { nodes, edges } = session.mindmap;
    
    if (nodes.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-gray-500 mb-4">Your mindmap is empty</p>
          <p className="text-gray-400 text-sm">Use commands like "/add-node [Name]" to build your mindmap</p>
          <p className="text-xs pl-5 pt-12 font-bold text-gray-300">© 2025 All rights Reserved <span className="text-xs font-normal font-ethnocentric">21st Century Edutech LLC</span>.</p>


        </div>
      );
    }
    
    return (
      <div ref={mindmapRef} className="p-4 bg-gray-50 relative overflow-auto" style={{ minHeight: '100%' }}>
        {/* Header with title and download button */}
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-50 z-10 pb-2">
          <h3 className="text-lg font-semibold text-gray-800">Design Competencies Map</h3>
          <button
          id="pdf-download-btn"
          onClick={handleDownloadPDF}
          className="px-4 py-2 bg-[#380A64] text-white rounded-lg hover:bg-yellow-500 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download PDF
        </button>
        </div>

        <div className="avoid-page-break">
          <MindmapVisualizer
            nodes={session?.mindmap?.nodes || []}
            edges={session?.mindmap?.edges || []}
            onNodeClick={handleNodeClick}
          />
        </div>
  
        <div className="avoid-page-break">
          <MindmapContent 
            session={session}
            frameworkPhases={frameworkPhases}
            activeSessionId={activeSessionId}
            sessions={sessions}
            setSessions={setSessions}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50 font-montserrat">
      {/* Left sidebar with sessions */}
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-2xl font-ethnocentric font-bold text-[#421270]">
            {isDiitriumMode ? "Dittrium" : "MindMap Chat"}
          </h2>
          <p className="text-xs font-bold text-[#d3af37] mb-2">
            {isDiitriumMode ? "Design Competency Explorer" : "Visual Conversation Tool"}
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto py-2 ">
          {sessions.map(session => (
            <div 
              key={session.id}
              className={`mx-3 mb-2 p-4 cursor-pointer flex justify-between items-center rounded-xl transition-all duration-200 hover:bg-purple-50 hover:shadow-sm ${
                session.id === activeSessionId ? 'bg-gradient-to-r from-purple-100 to-purple-50 border border-purple-200' : ''
              }`}
              onClick={() => setActiveSessionId(session.id)}
            >
              <span className="truncate text-[#421270] text-base font-medium">{session.title}</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSession(session.id);
                }}
                className="text-purple-400 hover:text-red-500 text-xl ml-3 transition-colors"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        
        <div className="p-4 border-t border-slate-100">
          {showNewSessionInput ? (
            <div className="flex flex-col space-y-3">
              <input
                type="text"
                value={newSessionTitle}
                onChange={(e) => setNewSessionTitle(e.target.value)}
                placeholder="Session name..."
                className="p-3 rounded-xl border border-slate-200 text-[#421270] text-base focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent"
              />
              <div className="flex justify-between space-x-2">
                <button 
                  onClick={handleCreateSession}
                  className="px-4 py-2 bg-gradient-to-r from-[#D3AF37] to-yellow-500 text-white rounded-lg text-sm font-medium hover:from-[#D3AF37] hover:to-yellow-600 transition-all shadow-sm"
                >
                  Create
                </button>
                <button 
                  onClick={() => setShowNewSessionInput(false)}
                  className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button 
              onClick={() => setShowNewSessionInput(true)}
              className="w-full p-3 bg-gradient-to-r rounded-xl text-[#380A64] hover:text-[#D3AF37] hover:scale-105 transition-all font-medium text-base shadow-sm"
            >
              + New Session
            </button>
          )}
        </div>
      </div>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Top bar with view toggle */}
        <div className="bg-white p-6 px-9 shadow-sm border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-bold text-2xl text-slate-800">{activeSession.title}</h3>
          
          <div className="flex space-x-3  bg-slate-100 p-1 rounded-xl">
            <button 
              onClick={() => setViewMode('chat')}
              className={`px-6 py-3 rounded-lg font-medium text-base transition-all ${
                viewMode === 'chat' 
                  ? 'bg-white text-[#380A64] shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Chat
            </button>
            <button 
              onClick={() => setViewMode('mindmap')}
              className={`px-6 py-3 rounded-lg font-medium text-base transition-all ${
                viewMode === 'mindmap' 
                  ? 'bg-white text-[#D3AF37] shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Mindmap
            </button>
          </div>
        </div>
        


        {/* Content area */}
        <div className="flex-1 overflow-auto">
          {viewMode === 'chat' && activeSession.mindmapGenerated === false ? (
            // Chat interface
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6 overflow-y-auto">
                {activeSession.messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-[#380A64] rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white text-xl">💬</span>
                      </div>
                      <p className="text-lg mb-2 text-slate-600">Start a new conversation</p>
                      <p className="text-base text-slate-400">Type /help to see available commands</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {activeSession.messages.map((message, index) => (
                      <div 
                        key={index} 
                        className={`max-w-md mb-4 p-4 rounded-2xl ${
                          message.sender === 'user' 
                            ? 'self-end ml-auto bg-gradient-to-r from-[#380A64] to-[#380A64] text-white rounded-br-lg shadow-sm' 
                            : 'self-start bg-white border border-slate-200 text-slate-700 rounded-bl-lg shadow-sm'
                        }`}
                      >
                        <div className="break-words">
                          <p className="text-base leading-relaxed">{message.text}</p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Multiple choice options for Diitrium mode */}
                    {isDiitriumMode && activeSession.multipleChoiceOptions && (
                      <div className="mb-6">
                        {activeSession.multipleChoiceOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleOptionSelect(option)}
                            className="w-full mb-3 p-4 text-left bg-white border border-slate-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all text-base text-slate-700 shadow-sm"
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Multiselect options for Diitrium mode */}
                    {isDiitriumMode && activeSession.multiselectOptions && (
                      <div className="mb-6">
                        {activeSession.multiselectOptions.map((option, index) => (
                          <div
                            key={index}
                            className="w-full mb-3 p-4 bg-white border border-slate-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all flex items-center shadow-sm"
                          >
                            <input
                              type="checkbox"
                              id={`option-${index}`}
                              checked={selectedOptions.includes(option)}
                              onChange={() => toggleOptionSelection(option)}
                              className="mr-4 h-5 w-5 text-[#380A64] border-slate-300 rounded focus:ring-[#421270]"
                            />
                            <label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-base text-[#421270]">
                              {option}
                            </label>
                          </div>
                        ))}
                        
                        {/* Submit button for multiselect */}
                        <button
                          onClick={handleMultiselectSubmit}
                          disabled={selectedOptions.length === 0}
                          className={`w-full p-4 text-center rounded-xl text-white font-medium text-base transition-all ${
                            selectedOptions.length === 0 
                              ? 'bg-slate-300 cursor-not-allowed' 
                              : 'bg-gradient-to-r from-[#D3AF37] to-yellow-500 hover:from-[#D3AF37] hover:to-yellow-600 shadow-sm'
                          }`}
                        >
                          Submit Selection{selectedOptions.length > 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                    
                    {/* Typing indicator */}
                    {isWaitingForResponse && (
                      <div className="self-start bg-white border border-slate-200 p-4 rounded-2xl rounded-bl-lg mb-4 shadow-sm">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <div className="px-6 py-4 border-t border-slate-100 bg-white">


              

              {
                activeSession.messages.length === 1 && activeSession.mindmapGenerated === false ? (
                  <div className="flex pt-2 flex-col items-center space-y-4 spaxe-x-4 px-20">
                    <button 
                      onClick={() => {
                        console.log("Clicked");

                        // Create a synthetic event-like object or directly call your message sending logic
                        const syntheticEvent = {
                          target: { value: "Start" },
                          preventDefault: () => {}
                        };
                        
                        // If you have access to the message sending logic, call it directly:
                        // sendMessage("Start");
                        
                        // Or temporarily set the input and use a proper state callback:
                        setInputValue("Start");
                        
                        // Use the callback form of setState and then submit
                        setTimeout(() => {
                          const event = new Event('submit');
                          handleSubmit(event);
                        }, 10);
                      }}
                      className="px-8 py-3 w-full bg-gradient-to-r from-[#421270] to-[#380A64] text-white rounded-2xl hover:from-[#380A64] hover:to-[#380A64] transition-all font-medium text-base shadow-sm"
                    >
                      Start
                    </button>
                    <button 
                      onClick={() => setViewMode('PDFUpload')}
                      className="px-8 py-3 bg-white w-full border-2 border-[#380A64] text-[#380A64] rounded-2xl hover:bg-purple-50 transition-all font-medium text-base shadow-sm"
                    >
                      Upload PDF
                    </button>

                    <p className="text-xs font-bold text-gray-500">© 2025 All rights Reserved <span className="text-xs font-normal font-ethnocentric">21st Century Edutech LLC</span>.</p>
                  </div>
                ) : activeSession.mindmapGenerated === false ? (
                  <div className="flex flex-col items-center space-y-4">
                    <div className="flex w-full space-x-4">
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={!isSendButtonEnabled()}
                        placeholder={
                          isDiitriumMode
                            ? "Type your message..."
                            : "Type a message or command... (try /help)"
                        }
                        className={`flex-1 py-2 px-5 border rounded-2xl focus:outline-none text-base ${
                          isSendButtonEnabled()
                            ? 'border-slate-200 text-slate-700 bg-slate-50 focus:ring-2 focus:ring-purple-300 focus:border-transparent'
                            : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      />
                      <button
                        onClick={handleSubmit}
                        disabled={!isSendButtonEnabled()}
                        className={`px-8 py-3 rounded-2xl font-medium text-base shadow-sm transition-all ${
                          isSendButtonEnabled()
                            ? 'bg-gradient-to-r from-[#380A64] to-[#380A64] text-white hover:from-[#380A64] hover:to-[#380A64] cursor-pointer'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Send
                      </button>
                    </div>

                    <p className="text-xs font-bold text-gray-300 text-center">
                      © 2025 All rights reserved{" "}
                      <span className="text-xs font-normal font-ethnocentric">
                        21st Century Edutech LLC
                      </span>.
                    </p>
                  </div>

                                    
                  
                ) : (
                  // Add what should render when mindmapGenerated is true
                  <div className="text-center text-slate-500">
                    Mindmap has been generated
                  </div>
                )
              }

            

                
              </div>
            </div>
          ) : viewMode === 'PDFUpload' && activeSession.mindmapGenerated === false && activeSession.mindmapRegenarated === false ? (
            console.log("PDFUpload component rendered",activeSession.pdfData),
            <div>
              <div
                className="
                  flex items-center gap-1 pt-4 pl-8 
                  cursor-pointer text-[#421270]
                  transition-colors duration-300
                  hover:text-yellow-500
                "
                onClick={() => setViewMode('chat')}
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back</span>
              </div>

            <PDFUpload setViewMode={setViewMode} setSessions={setSessions} sessions={sessions} activeSessionId={activeSessionId} pdfData = {activeSession.pdfData}/>
            </div>

            
          ) : viewMode === 'chat' && activeSession.mindmapGenerated === true && activeSession.mindmapGenerated === true && activeSession.mindmapRegenarated === false ? (
            <div className="flex flex-col h-full">
              <PDFUpload setViewMode={setViewMode} setSessions={setSessions} sessions={sessions} activeSessionId={activeSessionId} pdfData = {activeSession.pdfData}/>
            </div>


          ) : viewMode === 'chat' && activeSession.mindmapGenerated === true && activeSession.mindmapRegenarated === true ? (
            // Chat view with mindmap content
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="text-center text-slate-500">
                  Mindmap has been regenerated. No more regeneration available.
                </div>
              </div>
            </div>


          ) : viewMode === 'mindmap' && activeSession.mindmapGenerated === true ? (

            console.log("Mindmap view rendered",activeSessionId, sessions),


            // Mindmap view
            renderMindmap(sessions.find(session => session.id === activeSessionId),activeSessionId,sessions,setSessions)
          ) : renderMindmap(sessions.find(session => session.id === activeSessionId),activeSessionId,sessions,setSessions)
          
          
          }
        </div>
      </div>
    </div>
  );
}

export default App;